import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { DndProvider } from 'react-dnd';
import CarouselDnD from '../components/carousel/CarouselDnD.jsx';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import update from 'immutability-helper';
import { currentSession } from '../lib/auth-session';
import config from '../lib/config';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

export const isTouchDevice = () =>
  typeof window !== 'undefined' && 'ontouchstart' in window;

const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend;

// ---------------------------------------------------------------------------
// Dropzone
// ---------------------------------------------------------------------------
function ImageDropzone({ onDrop }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
  });

  return (
    <div
      {...getRootProps()}
      className={[
        'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 transition-colors cursor-pointer',
        isDragActive
          ? 'border-brand bg-brand-muted'
          : 'border-slate-200 bg-white hover:border-brand hover:bg-brand-muted',
      ].join(' ')}
    >
      <input {...getInputProps()} />
      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${isDragActive ? 'bg-brand text-white' : 'bg-slate-100 text-ink-muted'}`}>
        <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
      </div>
      <div className="text-center">
        <p className={`text-sm font-medium ${isDragActive ? 'text-brand' : 'text-ink'}`}>
          {isDragActive ? 'Drop to upload' : 'Upload new image'}
        </p>
        <p className="text-xs text-ink-muted">Drag & drop or click to browse</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function CarouselPage() {
  const [images, setImages] = useState([]);
  const [redirect, setRedirect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    currentSession()
      .then((user) =>
        fetch(`${config.BASE_URL}/carousel`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
          },
        }),
      )
      .then((res) => {
        if (res.status !== 200) {
          toast.error('Carousel not found or not authorized.');
          return null;
        }
        return res.json();
      })
      .then((response) => {
        if (!response || cancelled) return;
        setImages(
          response.CarouselItems.map((c) => ({
            id: c.Id,
            src: `${config.CAROUSEL_BASE}/${c.ImageId}`,
            CarouselItem: c,
          })),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err?.message?.includes?.('Not authenticated')
            ? 'Not logged in — redirecting.'
            : `Could not load carousel: ${String(err)}`,
        );
        setRedirect(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        currentSession()
          .then((user) =>
            fetch(`${config.BASE_URL}/carousel`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
              },
              body: JSON.stringify({ Image: e.target.result.split(',')[1] }),
            }),
          )
          .then((res) => {
            if (res.status > 201) {
              return res.json().then((json) => toast.error(`Upload failed: ${JSON.stringify(json)}`));
            }
            toast.success('Image uploaded');
            return res.json().then((json) => {
              setImages((prev) => [
                ...prev,
                {
                  id: json.Id,
                  src: e.target.result,
                  CarouselItem: {
                    ImageId: json.Id,
                    Index: prev.length,
                    Type: 'challenge',
                    Active: false,
                    Id: '',
                  },
                },
              ]);
            });
          })
          .catch((err) => toast.error(String(err)));
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const moveImage = (dragIndex, hoverIndex) => {
    const dragged = images[dragIndex];
    setImages(
      update(images, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragged],
        ],
      }),
    );
  };

  const onUpdateImage = useCallback((image) => {
    setImages((prev) => prev.map((img) => (img.id === image.id ? image : img)));
  }, []);

  const onDeleteImage = useCallback((image) => {
    currentSession()
      .then((user) =>
        fetch(`${config.BASE_URL}/carousel/${image.CarouselItem.ImageId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
          },
        }),
      )
      .then(() => {
        toast.success('Item deleted');
        setImages((prev) => prev.filter((img) => img.id !== image.id));
      })
      .catch((err) => toast.error(String(err)));
  }, []);

  const submitChange = () => {
    const unconfigured = images.filter((img) => img.CarouselItem.Id === '');
    if (unconfigured.length > 0) {
      toast.error('Every carousel item needs a challenge ID or category before saving.');
      return;
    }
    setSaving(true);
    currentSession()
      .then((user) =>
        fetch(`${config.BASE_URL}/carousel`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
          },
          body: JSON.stringify({
            CarouselItems: images.map((img, ind) => ({ ...img.CarouselItem, Index: ind })),
          }),
        }),
      )
      .then(() => toast.success('Carousel saved'))
      .catch((err) => toast.error(String(err)))
      .finally(() => setSaving(false));
  };

  if (redirect) return <Navigate to="/login" replace />;

  const unconfiguredCount = images.filter((img) => !img.CarouselItem.Id).length;
  const activeCount = images.filter((img) => img.CarouselItem.Active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Carousel</h1>
          <p className="text-sm text-ink-muted mt-0.5">
            Manage the images shown in the app's home screen carousel. Drag to reorder.
          </p>
        </div>
        <button
          type="button"
          onClick={submitChange}
          disabled={saving || loading}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Save changes
            </>
          )}
        </button>
      </div>

      {/* Stats bar */}
      {!loading && images.length > 0 && (
        <div className="flex gap-4">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-soft">
            <p className="text-xs text-ink-muted">Total items</p>
            <p className="text-lg font-semibold text-ink">{images.length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-soft">
            <p className="text-xs text-ink-muted">Active</p>
            <p className="text-lg font-semibold text-brand">{activeCount}</p>
          </div>
          {unconfiguredCount > 0 && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5">
              <p className="text-xs text-red-500">Not configured</p>
              <p className="text-lg font-semibold text-red-600">{unconfiguredCount}</p>
            </div>
          )}
        </div>
      )}

      {/* Upload */}
      <ImageDropzone onDrop={onDrop} />

      {/* Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <svg className="h-8 w-8 animate-spin text-brand" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-ink-muted">Loading carousel…</p>
        </div>
      ) : images.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
          <svg className="h-10 w-10 text-slate-300 mb-3" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 18h16.5M21 12V6.75A2.25 2.25 0 0018.75 4.5H5.25A2.25 2.25 0 003 6.75V12" />
          </svg>
          <p className="text-sm font-medium text-ink-muted">No carousel images yet</p>
          <p className="text-xs text-ink-muted mt-1">Upload an image above to get started</p>
        </div>
      ) : (
        <DndProvider backend={backendForDND}>
          <CarouselDnD
            images={images}
            callback={onUpdateImage}
            moveImage={moveImage}
            onDelete={onDeleteImage}
          />
        </DndProvider>
      )}
    </div>
  );
}
